'use client';
import { createContext, useContext, useState, ReactNode, FC, useCallback, useMemo, useRef, Fragment } from 'react';

type Modal = {
  key: string;
  content: ReactNode;
  withoutPadding?: boolean;
  containerClassName?: string;
  overlayClassName?: string;
  exclusive?: boolean;
  disableOverlayClose?: boolean;
};

type ModalContextProps = {
  modals: Modal[];
  open: (
    key: string,
    content: ReactNode,
    withoutPadding?: boolean,
    containerClassName?: string,
    overlayClassName?: string,
    exclusive?: boolean,
    disableOverlayClose?: boolean,
  ) => void;
  close: (key?: string) => void;
};

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const useModals = (): ModalContextProps => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModals должен использоваться внутри ModalProvider');
  }
  return context;
};

type ModalProviderProps = {
  children: ReactNode;
};

export const ModalProvider: FC<ModalProviderProps> = ({ children }) => {
  const [modals, setModals] = useState<Modal[]>([]);
  const exclusiveModalKeyRef = useRef<string | null>(null);

  const open = useCallback(
    (
      key: string,
      content: ReactNode,
      withoutPadding?: boolean,
      containerClassName?: string,
      overlayClassName?: string,
      exclusive?: boolean,
      disableOverlayClose?: boolean,
    ) => {
      // Prevent opening other modals while an exclusive modal is active
      if (exclusiveModalKeyRef.current && exclusiveModalKeyRef.current !== key) return;

      const nextModal: Modal = {
        key,
        content,
        withoutPadding,
        containerClassName,
        overlayClassName,
        exclusive,
        disableOverlayClose,
      };

      if (exclusive) {
        exclusiveModalKeyRef.current = key;
      }

      setModals([nextModal]);
    },
    [],
  );

  const close = useCallback((key?: string) => {
    if (!key) {
      exclusiveModalKeyRef.current = null;
      setModals((prevModals) => (prevModals.length ? [] : prevModals));
    } else {
      setModals((prevModals) =>
        prevModals.some((modal) => modal.key === key) ? prevModals.filter((modal) => modal.key !== key) : prevModals,
      );
      if (exclusiveModalKeyRef.current === key) {
        exclusiveModalKeyRef.current = null;
      }
    }
  }, []);

  const value = useMemo(() => ({ modals, open, close }), [modals, open, close]);

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};

// Каждый элемент modals.content — самодостаточный shadcn Dialog/Sheet
// (свой Portal, Overlay, анимации, backdrop-dismiss). ModalHost больше не
// рендерит собственный overlay/wrapper — раньше это создавало второй,
// конфликтующий слой поверх Dialog-компонента.
export const ModalHost: FC = () => {
  const { modals } = useModals();

  return (
    <>
      {modals.map((x) => (
        <Fragment key={x.key}>{x.content}</Fragment>
      ))}
    </>
  );
};
